import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "@tanstack/react-router";
import { Pointer } from "@/components/ui/pointer";
import { useTheme } from "@/components/themeProvider";
import { useMobile } from "@/providers/mobileProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";
import { DownloadButtons } from "@/components/ui/download-buttons";

export function ProgressHero() {
  const [heroImageSrc, setHeroImageSrc] = useState("/home/hero_light.webp");
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { t, tNode } = useTranslation();

  useEffect(() => {
    // Set image source based on theme
    setHeroImageSrc(isDark ? "/home/hero_dark.webp" : "/home/hero_light.webp");
  }, [isDark]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const additionalOffset = 24;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight - additionalOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="col-span-12 relative md:min-h-[calc(100svh-400px)] md:py-0"
    >
      <div className="container px-2 h-full">
        <div className="grid lg:grid-cols-2 h-full items-center gap-8 md:gap-0">
          {/* Content Column */}
          <div className="flex flex-col items-center lg:items-start justify-center relative z-10 w-full">
            <AnimatePresence>
              <>
                {/* Feature Badges */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="flex flex-wrap justify-center lg:justify-start gap-3 mb-4 w-full"
                >
                  {[
                    { icon: "🌱", label: t("home.hero.feature_badges.boost_energy") },
                    { icon: "💤", label: t("home.hero.feature_badges.transform_sleep") },
                    { icon: "💆‍♂️", label: t("home.hero.feature_badges.feel_better") },
                  ].map((feature, index) => (
                    <motion.div
                      key={feature.label}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.5,
                        ease: "easeOut",
                      }}
                      className="flex items-center gap-2 bg-background/80 backdrop-blur-md border border-border/40 rounded-full px-2.5 py-1 sm:px-3 sm:py-1.5 shadow-sm"
                    >
                      <span className="text-base sm:text-lg" role="img" aria-label={feature.label}>
                        {feature.icon}
                      </span>
                      <span className="text-xs sm:text-sm font-medium">{feature.label}</span>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Main heading */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="text-center lg:text-left w-full relative z-10 max-w-full mb-4"
                >
                  <Heading level="h1" size="h1" id="hero-heading" className="font-semibold">
                    {t("home.hero.heading")}
                  </Heading>
                </motion.div>

                {/* Subheading */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="text-balance text-lg sm:text-xl tracking-normal leading-tall text-muted-foreground/90 md:text-2xl mb-8 md:mb-12 text-center lg:text-left relative z-10 max-w-2xl"
                >
                  {tNode("home.hero.subheading", {
                    googleCalendar: (
                      <span className="font-semibold">{t("home.hero.google_calendar")}</span>
                    ),
                  })}
                </motion.p>

                {/* CTA Buttons */}
                <DownloadButtons isHero />

                {/* Social Proof Banner */}
                <motion.div
                  onClick={() => scrollToSection("social-proof")}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="w-fit mt-6 flex items-center justify-center lg:justify-start gap-3 text-sm text-muted-foreground rounded-full px-4 py-2 bg-muted/40 border border-border/40 backdrop-blur-sm hover:bg-muted/60 hover:border-border/60 transition-colors duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-block size-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="font-medium text-primary truncate">
                      {t("home.hero.social_proof.see_stories")}
                    </span>
                  </div>
                  <div className="w-px h-4 bg-border/40" />
                  <div className="flex items-center">
                    <div className="flex mr-1.5">
                      {["star1", "star2", "star3", "star4", "star5"].map((starId) => (
                        <svg
                          key={starId}
                          className="w-3.5 h-3.5 text-yellow-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="font-semibold">5.0</span>
                    <span className="ml-1 hidden md:block truncate">
                      {t("home.hero.social_proof.app_store_rating")}
                    </span>
                  </div>
                </motion.div>
              </>
            </AnimatePresence>
          </div>

          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="relative flex items-center justify-center lg:justify-end lg:pt-0 lg:mx-0 select-none"
          >
            <Pointer>
              <motion.div
                animate={{
                  scale: [0.8, 1, 0.8],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-pink-600"
                >
                  <title>Pointer</title>
                  <motion.path
                    d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                    fill="currentColor"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{
                      duration: 0.8,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                  />
                </svg>
              </motion.div>
            </Pointer>

            <div className="relative w-full max-w-[min(calc(100vw-1rem),500px)] sm:max-w-[min(calc(100vw-2rem),700px)] lg:max-w-[min(100%,1400px)] px-2 sm:px-4 lg:px-0">
              <div className="aspect-square relative max-h-[60vh] sm:max-h-[70vh] lg:max-h-none">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl blur-3xl -m-8" />
                <motion.div
                  whileTap={{ scale: 1.2 }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="absolute w-full h-full"
                >
                  <img
                    src={heroImageSrc}
                    alt={t("home.hero.image_alt")}
                    sizes="(max-width: 640px) 90vw, (max-width: 1200px) 55vw, 1000px"
                    className="w-full h-full object-contain drop-shadow-2xl select-none pointer-events-none"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
